/**
 * Parse open procurement arrays from product detail API responses.
 * Uses API fields only — do not infer from stock or catalogue flags.
 *
 * @param {Object|null|undefined} product
 * @returns {Array}
 */
export function getOpenQuotes(product) {
  return Array.isArray(product?.open_quotes) ? product.open_quotes : [];
}

/**
 * @param {Object|null|undefined} product
 * @returns {Array}
 */
export function getOpenOrders(product) {
  return Array.isArray(product?.open_orders) ? product.open_orders : [];
}

/**
 * True when an open_orders[] row still has in-flight line reception for this product.
 * Excludes clearly closed lines (defense against stale API rows when the order
 * remains open for other products). List-row counts still come from the API.
 *
 * @param {Object|null|undefined} row
 * @returns {boolean}
 */
export function isOpenOrderLine(row) {
  const lineStatus = String(row?.order_item_status ?? '').trim().toLowerCase();
  if (!lineStatus) return true;
  if (lineStatus === 'ok') return false;
  if (lineStatus.includes('fulfilled') && !lineStatus.includes('unfulfilled')) {
    return false;
  }
  return true;
}

/**
 * open_orders[] rows with in-flight line reception for this product.
 *
 * @param {Object|null|undefined} product
 * @returns {Array}
 */
export function getActiveOpenOrders(product) {
  return getOpenOrders(product).filter(isOpenOrderLine);
}

/**
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function hasOpenProcurement(product) {
  return getOpenQuotes(product).length > 0 || getActiveOpenOrders(product).length > 0;
}

/**
 * True when the row matched via same-supplier catalog-family (volume variant), not exact product.
 *
 * @param {Object|null|undefined} row - open_quotes[] or open_orders[] entry
 * @returns {boolean}
 */
export function isCatalogFamilyMatch(row) {
  return row?.match_type === 'catalog_family';
}

/**
 * Line product on the matched quote/order row (may differ from viewed product for variants).
 *
 * @param {Object|null|undefined} row
 * @returns {{ id: number, cat_num?: string, name?: string }|null}
 */
export function getLineProduct(row) {
  const lp = row?.line_product;
  return lp?.id != null ? lp : null;
}

/**
 * Normalize list/detail count fields from the API (non-negative integer).
 *
 * @param {unknown} value
 * @returns {number}
 */
export function parseProcurementCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return 0;
  }
  return Math.floor(n);
}

/**
 * Open quote count on product list rows (`GET /products/`).
 *
 * @param {Object|null|undefined} product
 * @returns {number}
 */
export function getOpenQuotesCount(product) {
  return parseProcurementCount(product?.open_quotes_count);
}

/**
 * Open order count on product list rows.
 *
 * @param {Object|null|undefined} product
 * @returns {number}
 */
export function getOpenOrdersCount(product) {
  return parseProcurementCount(product?.open_orders_count);
}

/**
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function hasOpenProcurementCounts(product) {
  return getOpenQuotesCount(product) > 0 || getOpenOrdersCount(product) > 0;
}

/**
 * Procurement-stage status for an open_quotes[] row (top-level quote status).
 *
 * @param {Object|null|undefined} row
 * @returns {string|null}
 */
export function getOpenQuoteProcurementStatus(row) {
  const status = row?.status;
  return status != null && String(status).trim() !== '' ? String(status) : null;
}

/**
 * Procurement-stage status for an open_orders[] row (linked quote status).
 * Do not use order_item_status — that is line reception, not procurement stage.
 *
 * @param {Object|null|undefined} row
 * @returns {string|null}
 */
export function getOpenOrderProcurementStatus(row) {
  const status = row?.quote?.status;
  return status != null && String(status).trim() !== '' ? String(status) : null;
}

/**
 * Line reception outcome on an open_orders[] row (secondary to quote.status).
 *
 * @param {Object|null|undefined} row
 * @returns {string|null}
 */
export function getOpenOrderLineReceptionStatus(row) {
  const status = row?.order_item_status;
  return status != null && String(status).trim() !== '' ? String(status) : null;
}

/**
 * Received quantity on an open_orders[] row (lots sum or order line qty).
 *
 * @param {Object|null|undefined} row
 * @returns {number|null}
 */
export function getOpenOrderReceivedQuantity(row) {
  if (row?.received_quantity != null) {
    return parseProcurementCount(row.received_quantity);
  }
  if (row?.quantity != null) {
    return parseProcurementCount(row.quantity);
  }
  return null;
}

/**
 * Ordered (quote line) quantity on an open_orders[] row.
 *
 * @param {Object|null|undefined} row
 * @returns {number|null}
 */
export function getOpenOrderOrderedQuantity(row) {
  if (row?.ordered_quantity == null) return null;
  return parseProcurementCount(row.ordered_quantity);
}

/**
 * Display label for open_orders[] Qty column: "received / ordered" or received only.
 *
 * @param {Object|null|undefined} row
 * @returns {string}
 */
export function formatOpenOrderQuantityLabel(row) {
  const received = getOpenOrderReceivedQuantity(row);
  const ordered = getOpenOrderOrderedQuantity(row);
  if (received == null && ordered == null) return '—';
  const r = received ?? 0;
  return ordered != null ? `${r} / ${ordered}` : String(r);
}

/**
 * Emoji for procurement / reception status labels (human-readable API strings).
 * More specific patterns are matched first (e.g. "sent to supplier" before "unfulfilled").
 *
 * @param {string|null|undefined} status
 * @returns {string|null}
 */
export function getProcurementStatusEmoji(status) {
  if (status == null) return null;
  const s = String(status).trim().toLowerCase();
  if (!s) return null;

  if (s.includes('sent to supplier')) return '🚗';
  if (s.includes('did not arrive')) return '📭';
  if (s.includes('arrived') && s.includes('unfulfilled')) return '📦';
  if (s.includes('fulfilled') && !s.includes('unfulfilled')) return '✅';
  if (s.includes('unfulfilled')) return '⏳';
  if (s.includes('requested')) return '📝';
  if (s.includes('received')) return '📬';
  if (s === 'ok') return '✅';
  if (s === 'other' || s.startsWith('other')) return '⚠️';
  if (s.includes('pending')) return '🕐';
  if (s.includes('cancelled') || s.includes('canceled')) return '🚫';
  if (s.includes('partial')) return '📊';

  return '📋';
}

/**
 * Visual highlight level for list rows and detail callouts.
 * Order wins when both open quotes and orders are present.
 *
 * @param {Object|null|undefined} product
 * @returns {'none' | 'quote' | 'order'}
 */
export function getProcurementHighlightLevel(product) {
  const hasOrders =
    getActiveOpenOrders(product).length > 0 || getOpenOrdersCount(product) > 0;
  const hasQuotes =
    getOpenQuotes(product).length > 0 || getOpenQuotesCount(product) > 0;
  if (hasOrders) return 'order';
  if (hasQuotes) return 'quote';
  return 'none';
}

/**
 * Short banner label for product table rows with open procurement.
 *
 * @param {'none' | 'quote' | 'order'} level
 * @returns {string|null}
 */
export function getProcurementRowBannerLabel(level) {
  if (level === 'order') return 'Ordered';
  if (level === 'quote') return 'Quoted';
  return null;
}
